import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import {
  createNurse,
  getFacilities,
  getStates,
  getDistricts,
  NURSE_ROLES,
} from "../api/nurseMasterApi";
import StoreSelectField, { toStoresPayload } from "./StoreSelectField";

/**
 * Add New Nurse — the form half of the Nurse Management screen, laid out like
 * FacilityUsers / DoctorsAddition (bootstrap form-control + react-hook-form).
 *
 * Creation goes through add_new_user/ with user_type "Nurse", the same
 * endpoint User Management posts to — it is what creates the login user
 * alongside the nurse record. Everything after creation (list / edit / delete)
 * uses the nurse-specific endpoints in nurseMasterApi.
 */

const USER_TYPE = "Nurse";

const SALUTATIONS = ["Mr", "Mrs", "Ms", "Miss", "Dr", "Prof"];

const GENDERS = [
  { value: "M", label: "Male" },
  { value: "F", label: "Female" },
  { value: "T", label: "Transgender" },
];

function NurseAddition({ updateList }) {
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues: { active: true } });

  const [facilityList, setFacilityList] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [dob, setDob] = useState(null);
  const [dateJoined, setDateJoined] = useState(null);

  const roleType = sessionStorage.getItem("roleType");
  // A facility admin can only add to their own facility, so the field is not
  // rendered and the value comes from the session — same rule DoctorsAddition
  // applies.
  const isFacilityAdmin = roleType === "facility admin";
  const sessionFacility = sessionStorage.getItem("facility_name");

  useEffect(() => {
    getStates()
      .then(setStates)
      .catch((err) => console.error("getStates:", err));

    if (!isFacilityAdmin) {
      getFacilities()
        .then(setFacilityList)
        .catch((err) => console.error("getFacilities:", err));
    }
  }, [isFacilityAdmin]);

  const handleStateChange = (stateName) => {
    setDistricts([]);
    if (!stateName) return;
    getDistricts(stateName)
      .then(setDistricts)
      .catch((err) => console.error("getDistricts:", err));
  };

  const onSubmit = async (data, e) => {
    e.preventDefault();

    const post_data = {
      user_type: USER_TYPE,
      facility_id: isFacilityAdmin ? sessionFacility || "" : data.facility_id,
      role: data.role ?? "",
      username: (data.username ?? "").trim(),
      email: (data.email ?? "").trim(),
      password: data.password ?? "",
      first_name: (data.first_name ?? "").trim(),
      last_name: (data.last_name ?? "").trim(),
      address: data.address ?? "",
      salutation: data.salutation ?? "",
      registration_no: data.registration_no ?? "",
      specialization: data.specialization ?? "",
      qualifications: data.qualifications ?? "",
      phone_no: data.phone_no ?? "",
      gender: data.gender ?? "",
      city: data.city ?? "",
      state: data.state ?? "",
      district: data.district ?? "",
      active: !!data.active,
    };

    // add_new_user/ rejects an empty `stores` ("Store is required") and treats a
    // missing key as "leave the assignment alone", so it is sent only when set.
    const storeIds = toStoresPayload(data.stores);
    if (storeIds.length) {
      post_data.stores = storeIds;
    }

    if (dob) {
      post_data.dob = dob.toISOString().split("T")[0];
    }
    if (dateJoined) {
      post_data.date_joined = dateJoined.toISOString().split("T")[0];
    }

    try {
      const res = await createNurse(post_data);
      if (res?.status === "Success") {
        toast.success(res?.message || "Nurse Added !!");
        reset({
          facility_id: "",
          role: "",
          salutation: "",
          first_name: "",
          last_name: "",
          username: "",
          password: "",
          email: "",
          phone_no: "",
          registration_no: "",
          specialization: "",
          qualifications: "",
          gender: "",
          address: "",
          state: "",
          district: "",
          city: "",
          stores: [],
          active: true,
        });
        setDistricts([]);
        setDob(null);
        setDateJoined(null);
        updateList();
      } else {
        toast.error(res?.message || "Could not add nurse");
      }
    } catch (err) {
      console.error("createNurse:", err);
      toast.error(err?.response?.data?.message || "Could not add nurse");
    }
  };

  return (
    <div>
      <div className="col-sm-12 col-md-12 col-lg-10" style={{ margin: "auto" }}>
        <div className="contact-panel mb-50" style={{ marginTop: "50px" }}>
          <h4 style={{ textAlign: "center" }}>Add New Nurse</h4>
          <form
            className="contact-panel__form"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="row">
              {!isFacilityAdmin && (
                <div className="col-sm-6 col-md-6 col-lg-6">
                  <label htmlFor="facility_id" className="required-label">
                    Facility{" "}
                  </label>
                  <div className="form-group">
                    <select
                      className="form-control"
                      name="facility_id"
                      {...register("facility_id", {
                        required: "Facility is required.",
                      })}
                    >
                      <option value="">Select Facility</option>
                      {facilityList.map((facility) => (
                        <option
                          key={facility.facility_id}
                          value={facility.facility_id}
                        >
                          {facility.facility_name}
                        </option>
                      ))}
                    </select>
                    {errors.facility_id && (
                      <p className="errorMsg" style={{ color: "red" }}>
                        {errors.facility_id.message}
                      </p>
                    )}
                  </div>
                </div>
              )}

              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="role" className="required-label">
                  Role{" "}
                </label>
                <div className="form-group">
                  <select
                    className="form-control"
                    name="role"
                    {...register("role", { required: "Role is required." })}
                  >
                    <option value="">Select Role</option>
                    {NURSE_ROLES.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                  {errors.role && (
                    <p className="errorMsg" style={{ color: "red" }}>
                      {errors.role.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="salutation">Salutation </label>
                <div className="form-group">
                  <select
                    className="form-control"
                    name="salutation"
                    {...register("salutation")}
                  >
                    <option value="">Select Salutation</option>
                    {SALUTATIONS.map((title) => (
                      <option key={title} value={title}>
                        {title}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="first_name" className="required-label">
                  First Name{" "}
                </label>
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter First Name"
                    name="first_name"
                    {...register("first_name", {
                      required: "First Name is required.",
                    })}
                  />
                  {errors.first_name && (
                    <p className="errorMsg" style={{ color: "red" }}>
                      {errors.first_name.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="last_name" className="required-label">
                  Last Name{" "}
                </label>
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Last Name"
                    name="last_name"
                    {...register("last_name", {
                      required: "Last Name is required.",
                    })}
                  />
                  {errors.last_name && (
                    <p className="errorMsg" style={{ color: "red" }}>
                      {errors.last_name.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="username" className="required-label">
                  User Name{" "}
                </label>
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter User Name"
                    name="username"
                    {...register("username", {
                      required: "User Name is required.",
                    })}
                  />
                  {errors.username && (
                    <p className="errorMsg" style={{ color: "red" }}>
                      {errors.username.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="password" className="required-label">
                  Password{" "}
                </label>
                <div className="form-group">
                  <input
                    type="password"
                    className="form-control"
                    placeholder="Enter Password"
                    name="password"
                    {...register("password", {
                      required: "Password is required.",
                    })}
                  />
                  {errors.password && (
                    <p className="errorMsg" style={{ color: "red" }}>
                      {errors.password.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="email" className="required-label">
                  Email{" "}
                </label>
                <div className="form-group">
                  <input
                    type="email"
                    className="form-control"
                    placeholder="Enter Email"
                    name="email"
                    {...register("email", { required: "Email is required." })}
                  />
                  {errors.email && (
                    <p className="errorMsg" style={{ color: "red" }}>
                      {errors.email.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="phone_no">Phone No </label>
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Phone No"
                    name="phone_no"
                    {...register("phone_no")}
                  />
                </div>
              </div>

              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="registration_no">Registration No </label>
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Registration No"
                    name="registration_no"
                    {...register("registration_no")}
                  />
                </div>
              </div>

              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="specialization">Specialization </label>
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Specialization"
                    name="specialization"
                    {...register("specialization")}
                  />
                </div>
              </div>

              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="qualifications">Qualifications </label>
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Qualifications"
                    name="qualifications"
                    {...register("qualifications")}
                  />
                </div>
              </div>

              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="gender">Gender </label>
                <div className="form-group">
                  <select
                    className="form-control"
                    name="gender"
                    {...register("gender")}
                  >
                    <option value="">Select Gender</option>
                    {GENDERS.map((g) => (
                      <option key={g.value} value={g.value}>
                        {g.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="dob">Date of Birth </label>
                <div className="form-group">
                  <DatePicker
                    selected={dob}
                    onChange={(date) => setDob(date)}
                    dateFormat="dd-MM-yyyy"
                    placeholderText="dd-mm-yyyy"
                    className="form-control"
                    name="dob"
                  />
                </div>
              </div>

              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="date_joined">Date Joined </label>
                <div className="form-group">
                  <DatePicker
                    selected={dateJoined}
                    onChange={(date) => setDateJoined(date)}
                    dateFormat="dd-MM-yyyy"
                    placeholderText="dd-mm-yyyy"
                    className="form-control"
                    name="date_joined"
                  />
                </div>
              </div>

              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="address">Address </label>
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Address"
                    name="address"
                    {...register("address")}
                  />
                </div>
              </div>

              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="state">State </label>
                <div className="form-group">
                  <select
                    className="form-control"
                    name="state"
                    {...register("state", {
                      onChange: (e) => handleStateChange(e.target.value),
                    })}
                  >
                    <option value="">Select State</option>
                    {states.map((state, index) => (
                      <option key={index} value={state.state_name}>
                        {state.state_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="district">District </label>
                <div className="form-group">
                  <select
                    className="form-control"
                    name="district"
                    {...register("district")}
                  >
                    <option value="">Select District</option>
                    {districts.map((district, index) => (
                      <option key={index} value={district.district_name}>
                        {district.district_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="city">City </label>
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter City"
                    name="city"
                    {...register("city")}
                  />
                </div>
              </div>

              <StoreSelectField control={control} />

              <div
                className="col-sm-3 col-md-3 col-lg-3"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "3%",
                }}
              >
                <div className="form-group">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      name="active"
                      {...register("active")}
                    />
                    <label className="form-check-label" htmlFor="active">
                      Active
                    </label>
                  </div>
                </div>
              </div>

              <div className="col-12 d-flex" style={{ padding: "0px" }}>
                <div className="col-6">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn btn__secondary btn__rounded btn__block mt-2"
                    style={{
                      backgroundColor: "#0E3F66",
                      color: "#FFFFFF",
                      borderRadius: "10px",
                      padding: "24px 140px",
                      gap: "10px",
                    }}
                  >
                    <span>Add Nurse</span> <i className="icon-arrow-right"></i>
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default NurseAddition;
